function normalizeVariant(variant) {
  const normalized = { ...variant };

  const hasInventoryQuantity =
    normalized.inventory_quantity !== null &&
    normalized.inventory_quantity !== undefined &&
    normalized.inventory_quantity !== '';

  normalized.mdid ||= normalized.featured_media?.id || null;

  normalized.inventory_quantity = hasInventoryQuantity
    ? Number(normalized.inventory_quantity)
    : null;

  normalized.quantity_rule ||= {
    min: 1,
    max: null,
    increment: 1,
    max4: true,
  };

  if (
    hasInventoryQuantity &&
    !normalized.quantity_rule.max &&
    normalized.inventory_quantity > 0 &&
    normalized.inventory_management &&
    normalized.inventory_policy !== 'continue'
  ) {
    normalized.quantity_rule.max = normalized.inventory_quantity;
    normalized.quantity_rule.max4 = true;
  }

  normalized.variant_state = {
    sold_out: !normalized.available,
    on_sale: normalized.compare_at_price > normalized.price,
    pre_order:
      hasInventoryQuantity &&
      normalized.inventory_management === 'shopify' &&
      normalized.inventory_quantity <= 0 &&
      normalized.available,
  };

  return normalized;
}

function getSelectedOptions(picker) {
  return [...picker.querySelectorAll('fieldset[data-index]')].map((fieldset) => {
    const selectedRadio = fieldset.querySelector('input[is-value]:checked');
    const hiddenInput = fieldset.querySelector('input[is-value][type="hidden"]');
    const selectedButton = fieldset.querySelector('[role="option"][aria-selected="true"]');
    return selectedRadio?.value || hiddenInput?.value || selectedButton?.value || '';
  });
}

function syncOptionControls(picker, options) {
  [...picker.querySelectorAll('fieldset[data-index]')].forEach((fieldset, index) => {
    const value = options[index];
    if (!value) return;

    fieldset.querySelectorAll('input[is-value][type="radio"]').forEach((input) => {
      input.checked = input.value === value;
    });

    const hiddenInput = fieldset.querySelector('input[is-value][type="hidden"]');
    if (hiddenInput) hiddenInput.value = value;

    fieldset.querySelectorAll('[role="option"]').forEach((option) => {
      option.setAttribute('aria-selected', String(option.value === value));
    });

    const richlist = fieldset.querySelector('hdt-richlist');
    if (richlist) richlist.setAttribute('selected', value);

    const selectButton = fieldset.querySelector(':scope > .hdt-relative > .hdt-select');
    if (selectButton) selectButton.textContent = value;

    const updateValue = fieldset.querySelector('[update-value]');
    if (updateValue) updateValue.textContent = value;
  });
}

function updateOptionStatuses(picker, variants, selectedOptions) {
  [...picker.querySelectorAll('fieldset[data-index]')].forEach((fieldset, index) => {
    fieldset.querySelectorAll('input[is-value], [role="option"]').forEach((control) => {
      const value = control.value;
      const candidates = variants.filter(
  (variant) =>
    variant.options[index] === value &&
    selectedOptions.every(
      (selected, optionIndex) =>
        optionIndex >= index ||
        !selected ||
        variant.options[optionIndex] === selected
    )
);
      const exists = candidates.length > 0;
      const available = candidates.some((variant) => variant.available);
      control.classList.toggle('is-unavailable', !exists);
      control.classList.toggle('is-disabled', exists && !available);
      control.closest('.hdt-selector__item, .hdt-swatch__item')?.classList.toggle('is-unavailable', !exists);
    });
  });
}

function findClosestVariant(variants, selectedOptions, changedIndex) {
  return variants
    .filter((variant) => variant.options[changedIndex] === selectedOptions[changedIndex])
    .sort((a, b) => {
      const score = (variant) => variant.options.reduce(
        (total, option, index) => total + (option === selectedOptions[index] ? 1 : 0),
        variant.available ? 1 : 0
      );
      return score(b) - score(a);
    })[0];
}

function updateStickyControl(picker, variant) {
  const sticky = picker.stickySelect;
  if (!sticky) return;

  if (sticky.tagName === 'SELECT') {
    const existingOption = [...sticky.options].find((option) => Number(option.value) === Number(variant.id));
    if (existingOption) sticky.value = String(variant.id);
  } else {
    const stickyForm = sticky.closest('form');
    const idInput = stickyForm?.querySelector('[name="id"]');
    if (idInput) idInput.value = variant.id;
  }
}

function enhanceVariantPicker(picker) {
  if (picker.dataset.faFastVariants === 'true') return;
  const dataElement = picker.querySelector('[data-fa-variants-json]');
  if (!dataElement) return;

  let variants;
  try {
    variants = JSON.parse(dataElement.textContent).map(normalizeVariant);
  } catch (error) {
    console.warn('Fast variant data could not be parsed.', error);
    return;
  }

  if (!variants.length) return;
  picker.dataset.faFastVariants = 'true';

  picker.getvariantObjById = (id) => variants.find((variant) => Number(variant.id) === Number(id));

  picker.updateOptions = async function updateOptionsInstantly() {
    let selectedOptions = getSelectedOptions(this);
    if (selectedOptions.filter(Boolean).length !== Number(this.dataset.size)) return;

    let variant = variants.find((candidate) =>
      candidate.options.every((option, index) => option === selectedOptions[index])
    );

    if (!variant) {
      variant = findClosestVariant(variants, selectedOptions, Number(this.clickedCurrentIndex || 0));
      if (variant) {
        selectedOptions = [...variant.options];
        syncOptionControls(this, selectedOptions);
      }
    }

    this.options = selectedOptions;
    this.currentVariant = variant || null;
    updateOptionStatuses(this, variants, selectedOptions);
    if (variant) updateStickyControl(this, variant);
  };

  picker.selectVariantByID = function selectVariantByIDInstantly(id, jumpMedia = true) {
    if (this.hasAttribute('no-pick')) return;
    const variant = variants.find((candidate) => Number(candidate.id) === Number(id));
    if (!variant) return;

    this.options = [...variant.options];
    this.currentVariant = variant;
    syncOptionControls(this, this.options);
    updateOptionStatuses(this, variants, this.options);
    this.selectVariant(false, jumpMedia, true);
  };
}

function buildProductActionRows(root = document) {
  const lists = new Set(root.querySelectorAll('.hdt-product-info__list'));
  if (root instanceof Element && root.matches('.hdt-product-info__list')) lists.add(root);
  if (root instanceof Element && root.closest('.hdt-product-info__list')) lists.add(root.closest('.hdt-product-info__list'));

  lists.forEach((list) => {
    const quantity = list.querySelector(':scope > [data-type="_quantity-selector"]');
    if (!quantity || quantity.parentElement?.classList.contains('fa-product-actions')) return;

    const buyButton = quantity.nextElementSibling;
    if (!buyButton?.matches('[data-type="_product-buy-button"]')) return;

    const row = document.createElement('div');
    row.className = 'fa-product-actions';
    quantity.before(row);
    row.append(quantity, buyButton);
  });
}

function enforcePersonalization(root = document) {
  const fields = new Set(root.querySelectorAll('input[type="text"], textarea'));
  if (root instanceof Element && root.matches('input[type="text"], textarea')) fields.add(root);

  fields.forEach((field) => {
    const label = field.labels?.[0]?.textContent || '';
    const context = `${field.name || ''} ${field.id || ''} ${field.getAttribute('aria-label') || ''} ${label}`.toLowerCase();
    if (!context.includes('add your name') && !context.includes('personaliz')) return;
    field.required = true;
    field.setAttribute('aria-required', 'true');
    if (field.maxLength < 0 || field.maxLength > 30) field.maxLength = 30;
  });
}

function enhance(root = document) {
  const pickers = new Set(root.querySelectorAll('hdt-variant-picker:not([data-fa-fast-variants="true"])'));
  if (root instanceof Element && root.matches('hdt-variant-picker:not([data-fa-fast-variants="true"])')) pickers.add(root);
  pickers.forEach(enhanceVariantPicker);
  buildProductActionRows(root);
  enforcePersonalization(root);
}

customElements.whenDefined('hdt-variant-picker').then(() => {
  enhance();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('hdt-variant-picker')) enhanceVariantPicker(node);
        enhance(node);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

document.addEventListener('shopify:section:load', (event) => enhance(event.target));

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;
  enforcePersonalization(form);
  const invalid = form.querySelector(':invalid');
  if (!invalid) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  invalid.reportValidity();
  invalid.focus();
}, true);

document.addEventListener('click', (event) => {
  const dialog = document.getElementById('SleepwearSizeGuide');
  const trigger = event.target.closest('a, button');
  if (!dialog || !trigger) return;
  if (trigger.matches('[data-fa-size-close]')) {
    dialog.close();
    return;
  }
  if (/size\s*guide/i.test(trigger.textContent || '')) {
    event.preventDefault();
    dialog.showModal();
  }
});
